import { collectAllPages } from "./pagination.js";
import {
  applyAccessToken,
  getAccessToken,
  getOrganizationId,
  readCsrfToken,
  setAccessToken,
  setOrganizationId,
} from "../session/session.js";
import type { ProjectListItem } from "../workspace/active-project.js";
import {
  ApiRequestError,
  type AcceptInvitationResult,
  type ArchiveClientInput,
  type ArchiveProjectInput,
  type ArchiveWebsiteInput,
  type ClientDetail,
  type ClientSummary,
  type CreateAttentionInput,
  type CreateAttentionResult,
  type CreateClientInput,
  type CreateClientResult,
  type CreateProjectInput,
  type CreateProjectResult,
  type CreateWebsiteInput,
  type CreateWebsiteResult,
  type InvitationSummary,
  type InviteMemberInput,
  type InviteMemberResult,
  type LinkWebsiteToProjectInput,
  type LinkWebsiteToProjectResult,
  type LoginResponse,
  type MembershipSummary,
  type OrganizationSummary,
  type Page,
  type ProjectDetail,
  type ProjectSummary,
  type RegisterUserInput,
  type RegisterUserResult,
  type ResolveAttentionInput,
  type ResolveAttentionResult,
  type RestoreClientInput,
  type RestoreProjectInput,
  type RestoreWebsiteInput,
  type RoleSummary,
  type UpdateClientInput,
  type UpdateClientResult,
  type UpdateProjectInput,
  type UpdateProjectResult,
  type UpdateWebsiteInput,
  type UpdateWebsiteResult,
  type WebsiteDetail,
  type WebsiteSummary,
} from "./types.js";

export { ApiRequestError } from "./types.js";

type HttpMethod = "GET" | "POST" | "PATCH";

interface RequestOptions {
  readonly method?: HttpMethod;
  readonly body?: unknown;
  readonly auth?: boolean;
  readonly csrf?: boolean;
}

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const headers = new Headers({ Accept: "application/json" });
  if (options.auth !== false) {
    const token = getAccessToken();
    if (token === null) {
      throw new ApiRequestError(401, "UNAUTHORIZED", "Not authenticated");
    }
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (options.csrf === true) {
    const csrf = readCsrfToken();
    if (csrf === null) {
      throw new ApiRequestError(401, "INVALID_CSRF_TOKEN", "CSRF token missing");
    }
    headers.set("x-csrf-token", csrf);
  }

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
  };
  // Only set JSON Content-Type when a body is sent. Bodyless POST with
  // application/json makes Fastify's JSON parser return 400 → VALIDATION_ERROR
  // ("Request could not be processed.") - e.g. org activate after login.
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(path, init);

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload: unknown = text.length === 0 ? null : JSON.parse(text);

  if (!response.ok) {
    const body = payload as { error?: string; message?: string } | null;
    throw new ApiRequestError(
      response.status,
      body?.error ?? "REQUEST_FAILED",
      body?.message ?? `Request failed (${String(response.status)})`,
    );
  }

  return payload as T;
};

export const login = async (
  email: string,
  password: string,
  activeOrganizationId?: string,
): Promise<LoginResponse> => {
  const body =
    activeOrganizationId === undefined
      ? { email, password }
      : { email, password, activeOrganizationId };
  const result = await apiRequest<LoginResponse>("/v1/auth/login", {
    method: "POST",
    body,
    auth: false,
  });
  applyAccessToken(result.accessToken);
  // Fallback if the issued token lacks `org` but login requested an active org.
  if (activeOrganizationId !== undefined && getOrganizationId() === null) {
    setOrganizationId(activeOrganizationId);
  }
  return result;
};

export const refreshSession = async (): Promise<LoginResponse> => {
  const result = await apiRequest<LoginResponse>("/v1/auth/refresh", {
    method: "POST",
    auth: false,
    csrf: true,
  });
  applyAccessToken(result.accessToken);
  return result;
};

export const logout = async (): Promise<undefined> => {
  try {
    await apiRequest<undefined>("/v1/auth/logout", { method: "POST" });
  } finally {
    setAccessToken(null);
    setOrganizationId(null);
  }
  return undefined;
};

export const listOrganizations = async (): Promise<readonly OrganizationSummary[]> => {
  const result = await apiRequest<unknown>("/v1/organizations");
  if (Array.isArray(result)) {
    return result as readonly OrganizationSummary[];
  }
  if (result !== null && typeof result === "object" && "items" in result) {
    const items = (result as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items as readonly OrganizationSummary[];
    }
  }
  return [];
};

export const activateOrganization = async (organizationId: string): Promise<LoginResponse> => {
  const result = await apiRequest<LoginResponse>(`/v1/organizations/${organizationId}/activate`, {
    method: "POST",
    csrf: true,
  });
  applyAccessToken(result.accessToken);
  // Activate path always binds this org; keep explicit id if claim decode fails.
  if (getOrganizationId() === null) {
    setOrganizationId(organizationId);
  }
  return result;
};

/** Identity session/setup write - Bearer only (no CSRF). Returns new org id. */
export const createOrganization = async (
  name: string,
  slug: string,
): Promise<{ readonly organizationId: string }> =>
  apiRequest<{ readonly organizationId: string }>("/v1/organizations", {
    method: "POST",
    body: { name, slug },
  });

const requireOrg = (): string => {
  const organizationId = getOrganizationId();
  if (organizationId === null) {
    throw new ApiRequestError(403, "ACTIVE_ORGANIZATION_REQUIRED", "Active organization required");
  }
  return organizationId;
};

export const searchClients = async (q: string): Promise<Page<ClientSummary>> => {
  const organizationId = requireOrg();
  const params = new URLSearchParams({ q });
  return apiRequest<Page<ClientSummary>>(
    `/v1/organizations/${organizationId}/clients?${params.toString()}`,
  );
};

/** Existing list contract without `q` — used for dashboard counts and local search. */
export const listAllClients = async (): Promise<readonly ClientSummary[]> => {
  const organizationId = requireOrg();
  return collectAllPages<ClientSummary>((cursor) => {
    const params = new URLSearchParams({
      status: "ACTIVE",
      limit: "50",
    });
    if (cursor !== null) {
      params.set("cursor", cursor);
    }
    return apiRequest<Page<ClientSummary>>(
      `/v1/organizations/${organizationId}/clients?${params.toString()}`,
    );
  });
};

export const listAllWebsites = async (): Promise<readonly WebsiteSummary[]> => {
  const organizationId = requireOrg();
  return collectAllPages<WebsiteSummary>((cursor) => {
    const params = new URLSearchParams({
      archived: "exclude",
      limit: "50",
    });
    if (cursor !== null) {
      params.set("cursor", cursor);
    }
    return apiRequest<Page<WebsiteSummary>>(
      `/v1/organizations/${organizationId}/websites?${params.toString()}`,
    );
  });
};

export const listAllProjects = async (): Promise<readonly ProjectSummary[]> => {
  const organizationId = requireOrg();
  return collectAllPages<ProjectSummary>((cursor) => {
    const params = new URLSearchParams({
      archived: "exclude",
      limit: "50",
    });
    if (cursor !== null) {
      params.set("cursor", cursor);
    }
    return apiRequest<Page<ProjectSummary>>(
      `/v1/organizations/${organizationId}/projects?${params.toString()}`,
    );
  });
};

export const getClient = async (clientId: string): Promise<ClientDetail> => {
  const organizationId = requireOrg();
  return apiRequest<ClientDetail>(`/v1/organizations/${organizationId}/clients/${clientId}`);
};

/** Create Client under the active organization (Bearer session; requires client.create). */
export const createClient = async (input: CreateClientInput): Promise<CreateClientResult> => {
  const organizationId = requireOrg();
  return apiRequest<CreateClientResult>(`/v1/organizations/${organizationId}/clients`, {
    method: "POST",
    body: { name: input.name, slug: input.slug },
  });
};

/** Update Client (Bearer; requires client.read + client.update; optimistic expectedVersion). */
export const updateClient = async (input: UpdateClientInput): Promise<UpdateClientResult> => {
  const organizationId = requireOrg();
  return apiRequest<UpdateClientResult>(
    `/v1/organizations/${organizationId}/clients/${input.clientId}`,
    {
      method: "PATCH",
      body: {
        name: input.name,
        expectedVersion: input.expectedVersion,
      },
    },
  );
};

/** Archive Client (Bearer; requires client.read + client.archive). Returns 204. */
export const archiveClient = async (input: ArchiveClientInput): Promise<undefined> => {
  const organizationId = requireOrg();
  return apiRequest<undefined>(
    `/v1/organizations/${organizationId}/clients/${input.clientId}/archive`,
    {
      method: "POST",
      body: { expectedVersion: input.expectedVersion },
    },
  );
};

/** Restore Client (Bearer; requires client.read + client.restore). Returns 204. */
export const restoreClient = async (input: RestoreClientInput): Promise<undefined> => {
  const organizationId = requireOrg();
  return apiRequest<undefined>(
    `/v1/organizations/${organizationId}/clients/${input.clientId}/restore`,
    {
      method: "POST",
      body: { expectedVersion: input.expectedVersion },
    },
  );
};

/** Create Website under the active organization (Bearer session; requires website.create). */
export const createWebsite = async (input: CreateWebsiteInput): Promise<CreateWebsiteResult> => {
  const organizationId = requireOrg();
  return apiRequest<CreateWebsiteResult>(`/v1/organizations/${organizationId}/websites`, {
    method: "POST",
    body: {
      clientId: input.clientId,
      name: input.name,
      domain: input.domain,
      ownerMembershipId: input.ownerMembershipId,
      environment: input.environment,
    },
  });
};

/** Update Website (Bearer; requires website.read + website.update; optimistic expectedVersion). */
export const updateWebsite = async (input: UpdateWebsiteInput): Promise<UpdateWebsiteResult> => {
  const organizationId = requireOrg();
  return apiRequest<UpdateWebsiteResult>(
    `/v1/organizations/${organizationId}/websites/${input.websiteId}`,
    {
      method: "PATCH",
      body: {
        name: input.name,
        domain: input.domain,
        expectedVersion: input.expectedVersion,
      },
    },
  );
};

/** Archive Website (Bearer; requires website.read + website.archive). Returns 204. */
export const archiveWebsite = async (input: ArchiveWebsiteInput): Promise<undefined> => {
  const organizationId = requireOrg();
  return apiRequest<undefined>(
    `/v1/organizations/${organizationId}/websites/${input.websiteId}/archive`,
    {
      method: "POST",
      body: { expectedVersion: input.expectedVersion },
    },
  );
};

/** Restore Website (Bearer; requires website.read + website.restore). Returns 204. */
export const restoreWebsite = async (input: RestoreWebsiteInput): Promise<undefined> => {
  const organizationId = requireOrg();
  return apiRequest<undefined>(
    `/v1/organizations/${organizationId}/websites/${input.websiteId}/restore`,
    {
      method: "POST",
      body: { expectedVersion: input.expectedVersion },
    },
  );
};

/** Create Project under the active organization (Bearer session; requires project.create). */
export const createProject = async (input: CreateProjectInput): Promise<CreateProjectResult> => {
  const organizationId = requireOrg();
  return apiRequest<CreateProjectResult>(`/v1/organizations/${organizationId}/projects`, {
    method: "POST",
    body: {
      clientId: input.clientId,
      name: input.name,
      ownerMembershipId: input.ownerMembershipId,
    },
  });
};

/** Update Project (Bearer; requires project.read + project.update; optimistic expectedVersion). */
export const updateProject = async (input: UpdateProjectInput): Promise<UpdateProjectResult> => {
  const organizationId = requireOrg();
  return apiRequest<UpdateProjectResult>(
    `/v1/organizations/${organizationId}/projects/${input.projectId}`,
    {
      method: "PATCH",
      body: {
        name: input.name,
        expectedVersion: input.expectedVersion,
      },
    },
  );
};

/** Archive Project (Bearer; requires project.read + project.archive). Returns 204. */
export const archiveProject = async (input: ArchiveProjectInput): Promise<undefined> => {
  const organizationId = requireOrg();
  return apiRequest<undefined>(
    `/v1/organizations/${organizationId}/projects/${input.projectId}/archive`,
    {
      method: "POST",
      body: { expectedVersion: input.expectedVersion },
    },
  );
};

/** Restore Project (Bearer; requires project.read + project.restore). Returns 204. */
export const restoreProject = async (input: RestoreProjectInput): Promise<undefined> => {
  const organizationId = requireOrg();
  return apiRequest<undefined>(
    `/v1/organizations/${organizationId}/projects/${input.projectId}/restore`,
    {
      method: "POST",
      body: { expectedVersion: input.expectedVersion },
    },
  );
};

/**
 * Link a Website to a Project (R1 enforced server-side).
 * Requires website.read + website.update; uses optimistic expectedVersion.
 */
export const linkWebsiteToProject = async (
  input: LinkWebsiteToProjectInput,
): Promise<LinkWebsiteToProjectResult> => {
  const organizationId = requireOrg();
  return apiRequest<LinkWebsiteToProjectResult>(
    `/v1/organizations/${organizationId}/websites/${input.websiteId}`,
    {
      method: "PATCH",
      body: {
        projectId: input.projectId,
        expectedVersion: input.expectedVersion,
      },
    },
  );
};

export const listWebsitesForClient = async (
  clientId: string,
): Promise<readonly WebsiteSummary[]> => {
  const organizationId = requireOrg();
  return collectAllPages<WebsiteSummary>((cursor) => {
    const params = new URLSearchParams({
      clientId,
      archived: "exclude",
      limit: "50",
    });
    if (cursor !== null) {
      params.set("cursor", cursor);
    }
    return apiRequest<Page<WebsiteSummary>>(
      `/v1/organizations/${organizationId}/websites?${params.toString()}`,
    );
  });
};

export const getWebsite = async (websiteId: string): Promise<WebsiteDetail> => {
  const organizationId = requireOrg();
  return apiRequest<WebsiteDetail>(`/v1/organizations/${organizationId}/websites/${websiteId}`);
};

export const getProject = async (projectId: string): Promise<ProjectDetail> => {
  const organizationId = requireOrg();
  return apiRequest<ProjectDetail>(`/v1/organizations/${organizationId}/projects/${projectId}`);
};

/**
 * Create OPEN Attention on a Project (Bearer session; requires project.read + project.attention.manage).
 * projectId is authoritative — never resolve by project name.
 */
export const createAttention = async (
  input: CreateAttentionInput,
): Promise<CreateAttentionResult> => {
  const organizationId = requireOrg();
  return apiRequest<CreateAttentionResult>(
    `/v1/organizations/${organizationId}/projects/${input.projectId}/attentions`,
    {
      method: "POST",
      body: {
        title: input.title,
        description: input.description,
        source: input.source,
      },
    },
  );
};

/**
 * Resolve an OPEN Attention (Bearer session; requires project.read + project.attention.manage).
 * OPEN → RESOLVED; already-resolved conflicts server-side.
 */
export const resolveAttention = async (
  input: ResolveAttentionInput,
): Promise<ResolveAttentionResult> => {
  const organizationId = requireOrg();
  return apiRequest<ResolveAttentionResult>(
    `/v1/organizations/${organizationId}/projects/${input.projectId}/attentions/${input.attentionId}/resolve`,
    {
      method: "POST",
    },
  );
};

/** Delta P2-D1 - exhaust project list before Active Project tiers. */
export const listAllProjectsForClient = async (
  clientId: string,
): Promise<readonly ProjectListItem[]> => {
  const items = await listProjectSummariesForClient(clientId);
  return items.map((item) => ({
    id: item.id,
    status: item.status,
    name: item.name,
    plannedCompletion: item.plannedCompletion,
    ownerMembershipId: item.ownerMembershipId,
    updatedAt: item.updatedAt,
  }));
};

/** Client-scoped project summaries (same list contract; keeps createdAt for Resumo). */
export const listProjectSummariesForClient = async (
  clientId: string,
): Promise<readonly ProjectSummary[]> => {
  const organizationId = requireOrg();
  return collectAllPages<ProjectSummary>((cursor) => {
    const params = new URLSearchParams({
      clientId,
      archived: "exclude",
      limit: "50",
    });
    if (cursor !== null) {
      params.set("cursor", cursor);
    }
    return apiRequest<Page<ProjectSummary>>(
      `/v1/organizations/${organizationId}/projects?${params.toString()}`,
    );
  });
};

export const listMembers = async (): Promise<readonly MembershipSummary[]> => {
  const organizationId = requireOrg();
  const result = await apiRequest<unknown>(`/v1/organizations/${organizationId}/members`);
  if (Array.isArray(result)) {
    return result as readonly MembershipSummary[];
  }
  if (result !== null && typeof result === "object" && "items" in result) {
    const items = (result as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items as readonly MembershipSummary[];
    }
  }
  return [];
};

export const listRoles = async (): Promise<readonly RoleSummary[]> => {
  const organizationId = requireOrg();
  const result = await apiRequest<unknown>(`/v1/organizations/${organizationId}/roles`);
  if (Array.isArray(result)) {
    return result as readonly RoleSummary[];
  }
  if (result !== null && typeof result === "object" && "items" in result) {
    const items = (result as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items as readonly RoleSummary[];
    }
  }
  return [];
};

/** Roles that may be assigned via invitation (owner is never invitable). */
export const listInviteableRoles = async (): Promise<readonly RoleSummary[]> =>
  (await listRoles()).filter((role) => role.key !== "owner");

export const listInvitations = async (): Promise<readonly InvitationSummary[]> => {
  const organizationId = requireOrg();
  const result = await apiRequest<unknown>(`/v1/organizations/${organizationId}/invitations`);
  if (Array.isArray(result)) {
    return result as readonly InvitationSummary[];
  }
  if (result !== null && typeof result === "object" && "items" in result) {
    const items = (result as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items as readonly InvitationSummary[];
    }
  }
  return [];
};

export const inviteMember = async (input: InviteMemberInput): Promise<InviteMemberResult> => {
  const organizationId = requireOrg();
  return apiRequest<InviteMemberResult>(`/v1/organizations/${organizationId}/invitations`, {
    method: "POST",
    body: {
      email: input.email,
      roleId: input.roleId,
      organizationName: input.organizationName,
      inviterName: input.inviterName,
    },
  });
};

export const acceptInvitation = async (token: string): Promise<AcceptInvitationResult> =>
  apiRequest<AcceptInvitationResult>("/v1/invitations/accept", {
    method: "POST",
    body: { token },
  });

export const registerUser = async (input: RegisterUserInput): Promise<RegisterUserResult> =>
  apiRequest<RegisterUserResult>("/v1/auth/register", {
    method: "POST",
    body: {
      email: input.email,
      password: input.password,
      displayName: input.displayName,
    },
    auth: false,
  });

export const verifyEmail = async (token: string): Promise<undefined> =>
  apiRequest<undefined>("/v1/auth/verify-email", {
    method: "POST",
    body: { token },
    auth: false,
  });
